import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchMeta, fetchPayload, checkAccessStatus, redeemAdvisorToken } from '../services/webReportApi';
import { applyBranding } from '../styles/applyBranding';
import { EmailGate } from './EmailGate';
import { DeckShell } from '../deck/DeckShell';
import { NotFoundPage } from './NotFoundPage';
import type { ReportEnrichment, WebReportMeta, WebReportPayload } from '../types/webReport';
import styles from './ReportPage.module.css';

type Stage = 'loading' | 'gate' | 'unlocked' | 'not-found' | 'expired';

export function ReportPage() {
  const { slug } = useParams<{ slug: string }>();

  const [stage, setStage] = useState<Stage>('loading');
  const [meta, setMeta] = useState<WebReportMeta | null>(null);
  const [payload, setPayload] = useState<WebReportPayload | null>(null);
  const [enrichment, setEnrichment] = useState<ReportEnrichment | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const m = await fetchMeta(slug);
        if (cancelled) return;
        setMeta(m);
        applyBranding(m.advisor?.branding);

        // Advisor bypass: the dashboard's "Open live plan" link carries a
        // short-lived ?at= token. Redeem it for an access cookie (skipping the
        // gate), then strip it from the URL so it doesn't linger in history or
        // get copied into a shared link. A stale/invalid token just no-ops and
        // the normal gate takes over below.
        const at = new URLSearchParams(window.location.search).get('at');
        if (at) {
          await redeemAdvisorToken(slug, at);
          if (cancelled) return;
          const params = new URLSearchParams(window.location.search);
          params.delete('at');
          const qs = params.toString();
          window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
        }

        // Probe with a non-401 endpoint first. Skips the gate when a 15-min
        // cookie is still valid; for first-time visitors it just returns
        // false instead of generating a noisy 401 in the browser console.
        const valid = await checkAccessStatus(slug);
        if (cancelled) return;
        if (!valid) {
          setStage('gate');
          return;
        }
        try {
          const p = await fetchPayload(slug);
          if (cancelled) return;
          setPayload(p.payload);
          setEnrichment(p.enrichment ?? null);
          setStage('unlocked');
        } catch {
          // Cookie was valid at probe time but rejected at payload time
          // (rare race). Fall back to the gate.
          if (!cancelled) setStage('gate');
        }
      } catch (err: any) {
        if (cancelled) return;
        if (err?.message === 'expired') setStage('expired');
        else setStage('not-found');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (!slug) return <NotFoundPage />;
  if (stage === 'not-found' || stage === 'expired') return <NotFoundPage />;

  if (stage === 'loading') {
    return (
      <div className={styles.center}>
        <div className={styles.dots} aria-label="Loading" />
      </div>
    );
  }

  if (stage === 'gate') {
    return (
      <EmailGate
        slug={slug}
        meta={meta}
        onUnlock={async () => {
          const p = await fetchPayload(slug);
          setPayload(p.payload);
          setEnrichment(p.enrichment ?? null);
          setStage('unlocked');
        }}
      />
    );
  }

  if (stage === 'unlocked' && payload && meta) {
    return <DeckShell slug={slug} payload={payload} enrichment={enrichment} advisor={meta.advisor} />;
  }

  return <NotFoundPage />;
}
